<template>
  <div class="schedule">
    <div class="schedule-calendar">
      <schedule-timeline :min-block-height="blockHeight"
                         :min-block-time="blockTime" />

      <schedule-day v-for="(day, index) in days"
                    :key="index"
                    :day="days[index]"
                    :child-id="childId"
                    :min-block-height="blockHeight"
                    :min-block-time="blockTime"
                    :current-date="currentDate"
                    @updateTimeBlocks="updateDailyTimeBlocks" />
    </div>
  </div>
</template>

<script>
  import ScheduleTimeline from './ScheduleTimeline.vue'
  import ScheduleDay from './ScheduleDay.vue'

  import { skipDuplicates } from '@/services/utils'
  import scheduleApi from '@/api/schedule.js'

  import analytics from '@/services/analytics.js'

  export default {
    props: {
      childId: {
        required: true
      }
    },

    components: {
      ScheduleTimeline,
      ScheduleDay
    },

    data() {
      return {
        // Minimal height of a time block
        blockHeight: 20,

        // Time in minutes for the shortest block
        // Any time period is divisible by this value
        blockTime: 15,

        // Array of days
        days: [
          {
            mask: '1000000',
            dayIndex: 0, // Pay attention that week starts from Sunday
            title: 'sunday',
            timeBlocks: []
          },
          {
            mask: '0100000',
            dayIndex: 1,
            title: 'monday',
            timeBlocks: []
          },
          {
            mask: '0010000',
            dayIndex: 2,
            title: 'tuesday',
            timeBlocks: []
          },
          {
            mask: '0001000',
            dayIndex: 3,
            title: 'wednesday',
            timeBlocks: []
          },
          {
            mask: '0000100',
            dayIndex: 4,
            title: 'thursday',
            timeBlocks: []
          },
          {
            mask: '0000010',
            dayIndex: 5,
            title: 'friday',
            timeBlocks: []
          },
          {
            mask: '0000001',
            dayIndex: 6,
            title: 'saturday',
            timeBlocks: [],
            serverTimeBlocks: null
          }
        ],

        currentDate: new Date(),
        currentDateUpdateTimerId: null
      }
    },

    created() {
      analytics.sendEvent('ScheduleEdit_Sc')
      this.loadTimeBlocks()
      this.setCurrentDateUpdateTimer()
    },

    mounted() {
      this.loadTimeBlocks()
    },

    methods: {
      loadTimeBlocks() {
        this.days.forEach(day => {
          day.timeBlocks = this.timeblocks
            .filter(timeBlock => {
              return timeBlock.mode_name === 'No Internet' || timeBlock.mode_name === 'Pause Device'
            })
            .filter(timeBlock => {
              return timeBlock.days[day.dayIndex] === '1'
            })
            .map(timeBlock => {
              return {
                ...timeBlock,
                days: day.mask
              }
            })
        })

        this.serverTimeBlocks = _.cloneDeep(this.currentTimeBlocks)
      },

      updateSchedule() {
        scheduleApi.update(this.childId, skipDuplicates(this.currentTimeBlocks))
          .then(response => {
            const { time_blocks, user_id } = response

            this.$store.dispatch('children/updateChildFromPublish', {
              child_id: user_id,
              data: {
                time_blocks
              }
            })

            this.showSuccessNotification()
          })
          .catch(() => {
            this.showFailNotification()
          })
      },

      updateDailyTimeBlocks(data) {
        const { day, timeBlocks } = data

        _.set(_.find(this.days, day), 'timeBlocks', timeBlocks)
      },

      setCurrentDateUpdateTimer() {
        this.currentDateUpdateTimerId = setInterval(this.setCurrentDate, 1000)
      },

      setCurrentDate() {
        this.currentDate = new Date()
      },

      clearCurrentDateUpdateTimer() {
        clearTimeout(this.currentDateUpdateTimerId)
      },

      showSuccessNotification() {
        this.$notify({
          type: 'success',
          text: 'Schedule successfully updated'
        })
      },

      showFailNotification() {
        this.$notify({
          type: 'error',
          text: 'Failed to update Schedule'
        })
      }
    },

    computed: {
      ...mapGetters('children', {
        getChildById: 'getById',
        getTimeblocks: 'getTimeblocks'
      }),

      timeblocks() {
        return this.getTimeblocks(this.childId)
      },

      child() {
        return this.getChildById(this.childId)
      },

      screentimeExhausted() {
        return !_.isNil(this.child.settings.screen_time_override_in_minutes) &&
          this.child.settings.screen_time_override_in_minutes <= 0
      },

      currentTimeBlocks() {
        const timeBlocks = _.flatten(this.days.map(day => day.timeBlocks))

        return timeBlocks.map(timeBlock => {
          return _.pick(timeBlock, ['days', 'start_time', 'duration_in_minutes', 'mode_id'])
        })
      },

      scheduleChanged() {
        return !_.isEqual(
          JSON.stringify(this.currentTimeBlocks),
          JSON.stringify(this.serverTimeBlocks)
        )
      }
    },

    watch: {
      timeblocks() {
        this.loadTimeBlocks()
      }
    },

    beforeDestroy() {
      this.clearCurrentDateUpdateTimer()
    },

    destroyed() {
      this.showModal('deviceAvailability', {
        childId: this.child.id,
        screentimeExhausted: this.screentimeExhausted,
        showScheduleBtn: false
      })
    }
  }
</script>

<style lang="scss" scoped>
  .schedule-calendar {
    display: flex;
    width: 100%;
  }
</style>
